import type { ZoneKind } from '../../../../reference-data/reference-data.api';
import type { Zone } from '../../../zones.api';
import type { ZoneGeoJsonPolygon } from '@chukua/common';
import { circlePolygon } from './circle-polygon';
import { polygonCentroid } from '../../editor/policies/polygon-centroid';
import { zoneMapBounds, type LngLatBounds } from './zone-map-bounds';

export interface ZoneFeatureProperties {
  zoneId: string;
  name: string;
  /** Kind code: the layers colour by it (zoneColorExpression). */
  kind: string;
  isActive: boolean;
  /** A zone's centre dot, which stands in for its polygon or circle while that is too small to see (zoneLayers). */
  isMarker: boolean;
}

type ZoneFeatureCollection<G extends GeoJSON.Geometry> = GeoJSON.FeatureCollection<G, ZoneFeatureProperties>;

/** The Zones map's two sources: polygons as drawn, and the dots (clustered when zoomed out) that stand for every zone. */
export interface ZonesMapSources {
  shapes: ZoneFeatureCollection<GeoJSON.Polygon>;
  points: ZoneFeatureCollection<GeoJSON.Point>;
}

function propertiesOf(zone: Zone, isMarker: boolean): ZoneFeatureProperties {
  return { zoneId: zone.id, name: zone.name, kind: zone.kind, isActive: zone.isActive, isMarker };
}

/** The area drawn for a zone: its polygon, or a point zone's circle. */
function areaOf(zone: Zone): ZoneGeoJsonPolygon | null {
  if (zone.polygon) return zone.polygon;
  return zone.centroid && zone.radiusMeters ? circlePolygon(zone.centroid, zone.radiusMeters) : null;
}

function pointOf(zone: Zone): GeoJSON.Feature<GeoJSON.Point, ZoneFeatureProperties> | null {
  const centre = zone.centroid ?? (zone.polygon ? polygonCentroid(zone.polygon) : null);
  if (!centre) return null;
  return { type: 'Feature', geometry: centre, properties: propertiesOf(zone, areaOf(zone) !== null) };
}

/**
 * A zone is its area (a polygon, or a point zone's circle) plus a marker dot at its centre, since most yards and
 * gates are too small to see until zoomed in; a zone with neither isn't drawn. The dots go in their own source
 * because MapLibre clusters only points, and Mombasa's thirty-odd zones pile up without it.
 */
export function zonesMapSources(zones: Zone[]): ZonesMapSources {
  const shapes = zones.flatMap((zone) => {
    const area = areaOf(zone);
    return area ? [{ type: 'Feature' as const, geometry: area, properties: propertiesOf(zone, false) }] : [];
  });
  const points = zones.map(pointOf).filter((feature) => feature !== null);
  return { shapes: { type: 'FeatureCollection', features: shapes }, points: { type: 'FeatureCollection', features: points } };
}

/** The area covering every zone that can be drawn, so the map opens on the results; null when none can be. */
export function zonesBounds(zones: Zone[]): LngLatBounds | null {
  const each = zones.map((zone) => zoneMapBounds(zone)).filter((bounds) => bounds !== null);
  if (each.length === 0) return null;
  return [
    [Math.min(...each.map(([[west]]) => west)), Math.min(...each.map(([[, south]]) => south))],
    [Math.max(...each.map(([, [east]]) => east)), Math.max(...each.map(([, [, north]]) => north))],
  ];
}

export interface KindCount {
  kind: ZoneKind;
  count: number;
}

/** The map legend: each kind among the zones on show, most common first. A zone whose kind isn't in the catalogue isn't listed. */
export function kindCounts(zones: Zone[], zoneKinds: ZoneKind[]): KindCount[] {
  const counts = new Map<string, number>();
  zones.forEach((zone) => counts.set(zone.kind, (counts.get(zone.kind) ?? 0) + 1));
  return zoneKinds
    .filter((kind) => counts.has(kind.code))
    .map((kind) => ({ kind, count: counts.get(kind.code) ?? 0 }))
    .sort((a, b) => b.count - a.count || a.kind.name.localeCompare(b.kind.name));
}
